/**
 * Tracks batch configuration and fetch lifecycle for LazyBatchLoader.
 *
 * Owns the configured batches, the reverse item→batch lookup, and the
 * runtime state that records which batches are fetched, in flight, or
 * have exhausted their retry budget.
 */
export class BatchRegistry {
  static readonly MAX_RETRIES = 3

  private batches: number[][] = []
  private itemToBatch = new Map<number, number>()
  private fetchedBatches = new Set<number>()
  private inflight = new Map<number, Promise<void>>()
  private failedBatchRetries = new Map<number, number>()

  /**
   * Replace the batch configuration and reset all lifecycle state so the
   * new batches can be fetched fresh.
   */
  load(batches: number[][]): void {
    this.batches = batches
    this.itemToBatch.clear()
    this.fetchedBatches.clear()
    this.inflight.clear()
    this.failedBatchRetries.clear()
    batches.forEach((batch, batchIdx) => {
      batch.forEach((itemIdx) => this.itemToBatch.set(itemIdx, batchIdx))
    })
  }

  batchFor(itemIdx: number): number | undefined {
    return this.itemToBatch.get(itemIdx)
  }

  batchItems(batchIdx: number): number[] {
    return this.batches[batchIdx] ?? []
  }

  get allBatches(): readonly number[][] {
    return this.batches
  }

  batchCount(): number {
    return this.batches.length
  }

  /**
   * True if the batch is already fetched, currently in flight, or has
   * exceeded its retry budget.
   */
  isHandled(batchIdx: number): boolean {
    return (
      this.fetchedBatches.has(batchIdx) ||
      this.inflight.has(batchIdx) ||
      (this.failedBatchRetries.get(batchIdx) ?? 0) >= BatchRegistry.MAX_RETRIES
    )
  }

  getInflight(batchIdx: number): Promise<void> | undefined {
    return this.inflight.get(batchIdx)
  }

  inflightPromises(): Promise<void>[] {
    return [...this.inflight.values()]
  }

  inflightCount(): number {
    return this.inflight.size
  }

  markInflight(batchIdx: number, promise: Promise<void>): void {
    this.inflight.set(batchIdx, promise)
  }

  markFetched(batchIdx: number): void {
    this.fetchedBatches.add(batchIdx)
  }

  markFailed(batchIdx: number): void {
    const retries = (this.failedBatchRetries.get(batchIdx) ?? 0) + 1
    this.failedBatchRetries.set(batchIdx, retries)
  }

  clearInflight(batchIdx: number): void {
    this.inflight.delete(batchIdx)
  }

  /**
   * Clear the runtime fetch state (fetched, in-flight, retries) while
   * preserving the configured batches. Used by `LazyBatchLoader.destroy()`
   * so a subsequent `init()` refetches from a clean slate.
   */
  resetRuntime(): void {
    this.fetchedBatches.clear()
    this.inflight.clear()
    this.failedBatchRetries.clear()
  }
}
